import {
  Add as AddIcon,
  FolderSpecial as ArtifactsIcon,
  ErrorOutline as ErrorIcon,
  Menu as MenuIcon,
  Refresh as RefreshIcon,
  Schedule as SchedulesIcon,
} from "@mui/icons-material";
import {
  Alert,
  AlertTitle,
  Badge,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Drawer,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  type ImperativePanelHandle,
  Panel,
  PanelGroup,
  PanelResizeHandle,
} from "react-resizable-panels";
import { useSearchParams } from "react-router";
import { ArgusConnectionChip } from "../components/argus/argus-connection-chip";
import { ArgusInstanceControls } from "../components/argus/argus-instance-controls";
import { ArgusMessageInput } from "../components/argus/argus-message-input";
import { ArgusMessageList } from "../components/argus/argus-message-list";
import { ArgusRelatedResourcesPanel } from "../components/argus/argus-related-resources-panel";
import { ArgusReportDrawer } from "../components/argus/argus-report-drawer";
import { ArgusSchedulesDialog } from "../components/argus/argus-schedules-dialog";
import { ArgusSessionListPanel } from "../components/argus/argus-session-list-panel";
import { DespleaIcon } from "../components/icons/desplega-icon";
import {
  useArgusInstance,
  useArgusInstances,
  useArgusSession,
  useArgusSessionHeartbeat,
  useArgusSessions,
  useAutoResumeArgusInstance,
  useBootArgusInstance,
  useHealArgusInstance,
  useUpdateArgusInstance,
} from "../hooks/argus";
import { useApiMutation } from "../hooks/use-api-mutation";
import { useQueryParams } from "../hooks/use-query-params";
import { track } from "../lib/analytics.ts";
import { stripContextPrefix } from "../lib/argus/argus-text";
import { useArgusConnectionContext } from "../providers/argus-connection-provider";
import { RADIUS_LG } from "../theme/theme";
import type { OnboardingProvisionResponse } from "../types/argus";

/**
 * Shared header strip height (in px) for the session list, conversation
 * panel, and artifacts panel. Locked so the bottom dividers of all three
 * headers sit on the same horizontal baseline regardless of each panel's
 * own header content.
 */
const HEADER_HEIGHT = 56;

/**
 * `/argus` page — three-panel chat UI.
 *
 * Pencil: Frame 1 (`CA7pb`), Frame 4 (`UCFTf`).
 *
 * Layout:
 *   - Left:   session list + "New session" button
 *   - Center: instance controls (header) + message list + input
 *   - Right:  placeholder "Related resources" column (wired in Phase 5)
 *
 * States:
 *   - No instance                  → zero-state CTA that creates + boots an instance
 *   - Instance booting              → spinner with "provisioning…" copy
 *   - Instance paused / inactive    → shows controls but disables the input
 *   - Instance error                → shows `boot_error` + retry button
 *   - Instance active               → full three-panel UI, SSE connected
 */
const ArgusPage = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [searchParams, setSearchParams] = useSearchParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const artifactsPanelRef = useRef<ImperativePanelHandle | null>(null);
  const sessionsPanelRef = useRef<ImperativePanelHandle | null>(null);
  const [sessionsCollapsed, setSessionsCollapsed] = useState(
    () => searchParams.get("sessions") === "0",
  );
  const [artifactsCollapsed, setArtifactsCollapsed] = useState(
    () => searchParams.get("artifacts") !== "1",
  );
  // F36: panel-derived count drives both the badge on the collapsed
  // IconButton and the one-shot auto-expand on first artifact per session.
  // Single source of truth for the count is the panel itself
  // (`argus-related-resources-panel.tsx:142`); the page just consumes it.
  const [artifactCount, setArtifactCount] = useState(0);
  const hasAutoExpandedRef = useRef(false);
  const autoExpandSessionRef = useRef<string | null>(null);
  // Mobile-only drawers for viewing the sessions list and artifacts panel.
  // Desktop uses the resizable inline Panels instead.
  const [sessionListDrawerOpen, setSessionListDrawerOpen] = useState(false);
  const [artifactsDrawerOpen, setArtifactsDrawerOpen] = useState(false);
  const [reportDrawerOpen, setReportDrawerOpen] = useState(false);
  const [schedulesDialogOpen, setSchedulesDialogOpen] = useState(false);
  // Gating the destructive update behind a confirm dialog — clicking the
  // "Update available" button reboots the sandbox and ends any running
  // session, so a single accidental click should not nuke active work.
  const [confirmUpdateOpen, setConfirmUpdateOpen] = useState(false);

  const {
    activeSessionId,
    setActiveSessionId,
    pendingSessionPreview,
    optimisticUserMessages,
    isAwaitingResponse,
    isTranscriptLoading,
    isReadOnly,
    isAgentWorking,
    wasStopped,
    queuedMessages,
    clearQueue,
    removeFromQueue,
    promoteToSteer,
    events,
    seededMessages,
    hydratedOpenToolCallIds,
    isConnected,
    connectionError,
    sendMessage,
    startSession,
    stopSession,
    instanceStatus,
  } = useArgusConnectionContext();

  // Own instance queries so we can render zero-state CTA without leaning on
  // the provider (the provider is always mounted — not all of its fields are
  // populated until there's an instance).
  const { instances, isLoading: instancesLoading } = useArgusInstances({
    limit: 1,
  });
  const instanceId = instances?.[0]?.id;
  const { instance } = useArgusInstance(instanceId, { refetchInterval: 5_000 });
  const { sessions } = useArgusSessions(instanceId);

  // Layer C: while this page is watching a non-terminal session, heartbeat
  // the backend so it refreshes the e2b sandbox pause window. Resolve the
  // DB session_id explicitly (activeSessionId can be either the agent
  // session id or the DB id depending on where it came from); the heartbeat
  // endpoint keys off the DB id.
  const heartbeatSession = activeSessionId
    ? sessions?.find((s) => s.id === activeSessionId || s.agent_session_id === activeSessionId)
    : undefined;
  useArgusSessionHeartbeat({
    sessionId: heartbeatSession?.id,
    sessionStatus: heartbeatSession?.session_status,
  });
  // Single-session GET is the only endpoint that returns `schedule` info
  // (list query skips the join for perf). Called unconditionally at the
  // top so React's rules-of-hooks are satisfied — the underlying
  // useApiQuery is gated by the truthy session id via its `enabled` opt.
  const { session: detailSession } = useArgusSession(heartbeatSession?.id);

  // Derive live context fill from the SSE stream so the input's
  // progress indicator updates in real time during a turn — the
  // backend-persisted `detailSession.context_tokens` only refreshes on
  // query refetch, which would lag behind the active conversation.
  //
  // Uses `input + cacheRead` rather than `input` alone: with prompt
  // caching, `input` reports only the uncached delta and swings between
  // calls. `input + cacheRead` is the true tokens sent to the model
  // and grows monotonically within a turn.
  const liveContextTokens = useMemo(() => {
    for (let i = events.length - 1; i >= 0; i--) {
      const ev = events[i];
      if ((ev as { type?: string })?.type !== "message_end") continue;
      const msg = (
        ev as {
          message?: {
            role?: string;
            usage?: { input?: number; cacheRead?: number };
          };
        }
      ).message;
      if (msg?.role !== "assistant" || !msg.usage) continue;
      const total = (msg.usage.input ?? 0) + (msg.usage.cacheRead ?? 0);
      if (total > 0) return total;
    }
    return undefined;
  }, [events]);

  const boot = useBootArgusInstance();
  const heal = useHealArgusInstance();
  const update = useUpdateArgusInstance();
  const { getParam } = useQueryParams();
  const isDebug = getParam("debug") === "true";

  // Phase 5 reverse-link: `/argus?session=X` preloads the session.
  // Only on initial mount — after that, handleSelectSession/handleNewSession
  // own the URL↔state sync to avoid race conditions.
  const initialSessionSynced = useRef(false);
  useEffect(() => {
    if (initialSessionSynced.current) return;
    const requested = searchParams.get("session");
    if (requested && requested !== activeSessionId) {
      setActiveSessionId(requested);
    }
    initialSessionSynced.current = true;
  }, [searchParams, activeSessionId, setActiveSessionId]);

  // Clear stale ?session= param when the instance changes (e.g. after
  // instance recreation). Old sessions don't exist on the new sandbox.
  // Initial undefined→id transition (instances query resolving) must NOT
  // count as a change — otherwise the CTA hop from /onboarding loses
  // `?session=` as soon as useArgusInstances resolves.
  const prevInstanceIdRef = useRef<string | undefined>(instanceId);
  useEffect(() => {
    const prev = prevInstanceIdRef.current;
    if (prev === instanceId) return;
    prevInstanceIdRef.current = instanceId;
    if (prev === undefined) return;
    if (activeSessionId) {
      setActiveSessionId(null);
      setSearchParams(
        (p) => {
          const next = new URLSearchParams(p);
          next.delete("session");
          return next;
        },
        { replace: true },
      );
    }
  }, [instanceId, activeSessionId, setActiveSessionId, setSearchParams]);

  // Home page prompt: `/argus?prompt=<message>` starts a new session
  // with the given message. Consumed once on mount.
  // Clear the prompt param from URL immediately (even while booting) so
  // it doesn't linger in the address bar. The actual session start waits
  // for the instance to be active.
  const promptConsumed = useRef(false);
  const pendingPrompt = useRef<string | null>(null);
  useEffect(() => {
    const prompt = searchParams.get("prompt");
    if (!prompt) return;
    // Always clear the prompt param from URL immediately.
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("prompt");
        return next;
      },
      { replace: true },
    );
    // Stash for later if the instance isn't active yet.
    pendingPrompt.current = prompt;
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (promptConsumed.current) return;
    const prompt = pendingPrompt.current;
    if (!prompt || instanceStatus !== "active") return;
    promptConsumed.current = true;
    pendingPrompt.current = null;
    // Clear any prior session so the UI resets before starting fresh.
    setActiveSessionId(null);
    void startSession(prompt);
  }, [instanceStatus, setActiveSessionId, startSession]);

  // Keep URL in sync whenever activeSessionId changes from any source
  // (SSE session-start, home page sendMessage, etc.)
  useEffect(() => {
    if (!initialSessionSynced.current) return;
    const current = searchParams.get("session");
    if (activeSessionId && current !== activeSessionId) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set("session", activeSessionId);
          return next;
        },
        { replace: true },
      );
    } else if (!activeSessionId && current) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete("session");
          return next;
        },
        { replace: true },
      );
    }
  }, [activeSessionId, searchParams, setSearchParams]);

  // Keep URL query param in sync when user clicks a session.
  const handleSelectSession = useCallback(
    (sessionId: string) => {
      setActiveSessionId(sessionId);
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set("session", sessionId);
          return next;
        },
        { replace: true },
      );
    },
    [setActiveSessionId, setSearchParams],
  );

  const handleNewSession = useCallback(() => {
    setActiveSessionId(null);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("session");
        return next;
      },
      { replace: true },
    );
  }, [setActiveSessionId, setSearchParams]);

  // Restore panel collapsed state from query params on mount. Both
  // `?sessions=0` and `?artifacts=0` (or the default-collapsed artifacts
  // path) force the corresponding panel to collapse on mount — this is
  // a hard dependency for the onboarding CTA which navigates with
  // `?sessions=0&artifacts=0` so the user lands on a focused chat view.
  //
  // `autoSaveId="argus-layout"` on the PanelGroup restores persisted
  // sizes on mount, which can leave the sessions panel visibly
  // non-collapsed even after `.collapse()` is called on the first
  // paint. The explicit `.collapse()` calls below are applied via an
  // effect so they run after the panels mount + read their saved
  // state, which guarantees the URL-param intent wins.
  const panelStateSynced = useRef(false);
  useEffect(() => {
    if (panelStateSynced.current) return;
    panelStateSynced.current = true;
    if (searchParams.get("sessions") === "0") {
      sessionsPanelRef.current?.collapse();
    }
    // Artifacts panel is collapsed by default — only expand if explicitly requested.
    if (searchParams.get("artifacts") === "1") {
      artifactsPanelRef.current?.expand();
    } else {
      artifactsPanelRef.current?.collapse();
    }
  }, [searchParams]);

  // Auto-collapse both panels when entering an onboarding session so the
  // welcome card + chat are the visual focus. User can still manually
  // expand either panel after mount — this only forces the initial
  // default. Re-runs when the active session changes (e.g. user clicks a
  // non-onboarding session in the list, then back to the onboarding
  // one).
  const isOnboardingSession =
    (detailSession?.config as { is_onboarding?: boolean } | null | undefined)?.is_onboarding ===
    true;
  const onboardingCollapseSyncedFor = useRef<string | null>(null);
  useEffect(() => {
    if (!isOnboardingSession || !activeSessionId) return;
    if (onboardingCollapseSyncedFor.current === activeSessionId) return;
    onboardingCollapseSyncedFor.current = activeSessionId;
    sessionsPanelRef.current?.collapse();
    artifactsPanelRef.current?.collapse();
  }, [isOnboardingSession, activeSessionId]);

  // F36: re-arm the auto-expand guard whenever the active session changes
  // so the next session's first artifact pops the panel back open even if
  // the user previously collapsed it. Mirrors the
  // `prevInstanceIdRef`-style change detection used elsewhere in this
  // file.
  useEffect(() => {
    if (autoExpandSessionRef.current === activeSessionId) return;
    autoExpandSessionRef.current = activeSessionId;
    hasAutoExpandedRef.current = false;
  }, [activeSessionId]);

  // F36: one-shot auto-expand on the 0 → >0 artifact transition for the
  // active session. Per-session ref guard means a manual collapse after
  // auto-open is respected — we won't re-pop the panel on subsequent
  // artifact additions in the same session.
  useEffect(() => {
    if (artifactCount <= 0 || hasAutoExpandedRef.current) return;
    hasAutoExpandedRef.current = true;
    if (isMobile) {
      setArtifactsDrawerOpen(true);
    } else {
      artifactsPanelRef.current?.expand();
    }
  }, [artifactCount, isMobile]);

  const autoResume = useAutoResumeArgusInstance({
    instanceId: instance?.id,
    instanceStatus: instance?.instance_status,
    enabled: !isDebug,
  });

  // --- Zero-state: no instance yet ---
  //
  // Post Phase 4: onboarding v2 owns the "first instance" flow via
  // `/onboarding`. Users who have completed onboarding but currently have zero
  // instances (e.g. admin deleted the instance manually) get a minimal
  // empty-state with a link to re-provision.
  //
  // Gate on `instanceId` (from `useArgusInstances`), NOT on `instance` (from
  // `useArgusInstance`). Otherwise there's a brief window after the CTA hop
  // from /onboarding where instances-list has resolved and set instanceId,
  // but the single-instance query hasn't populated yet → `!instance` was
  // true → zero-state fired → the user bounced back to /onboarding on their
  // first CTA click. See F15 in the onboarding-v2 QA report.
  if (!instancesLoading && !instanceId) {
    return <ArgusZeroStateEmpty />;
  }

  // --- Boot-in-progress blocking state ---
  if (instance && instance.instance_status === "booting") {
    return (
      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 4,
        }}
      >
        <Stack alignItems="center" spacing={2}>
          <CircularProgress />
          <Typography variant="h6">Provisioning your sandbox…</Typography>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            This usually takes a minute or two.
          </Typography>
        </Stack>
      </Box>
    );
  }

  // --- Paused blocking state (non-debug only) ---
  //
  // Regular users never see a "Resume" button — the paused state is treated
  // as an implementation detail and we auto-resume transparently (see the
  // effect above). In debug mode we skip this full-page state so the
  // ArgusInstanceControls header still renders, giving access to the
  // manual Resume / Pause / Delete buttons.
  if (instance && instance.instance_status === "paused" && !isDebug) {
    return (
      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 4,
        }}
      >
        <Stack alignItems="center" spacing={2}>
          {autoResume.hasReachedLimit ? (
            <>
              <ErrorIcon color="error" />
              <Typography variant="h6">Couldn't reconnect</Typography>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                Argus couldn't resume the sandbox automatically.
              </Typography>
              <Button
                variant="contained"
                aria-label="Retry reconnecting to Argus"
                onClick={autoResume.retry}
              >
                Retry
              </Button>
            </>
          ) : (
            <>
              <CircularProgress />
              <Typography variant="h6">Resuming your sandbox…</Typography>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                This usually takes just a moment.
              </Typography>
            </>
          )}
        </Stack>
      </Box>
    );
  }

  // --- Error state ---
  if (instance && instance.instance_status === "error") {
    return (
      <Box sx={{ padding: 4, display: "flex", justifyContent: "center" }}>
        <Paper
          variant="outlined"
          sx={{ maxWidth: 560, padding: 3, borderRadius: `${RADIUS_LG}px` }}
        >
          <Stack direction="row" spacing={1} alignItems="center" sx={{ marginBottom: 2 }}>
            <ErrorIcon color="error" />
            <Typography variant="h6">Argus instance failed to boot</Typography>
          </Stack>
          {instance.boot_error && (
            <Alert severity="error" sx={{ marginBottom: 2 }}>
              {instance.boot_error}
            </Alert>
          )}
          <Button
            variant="contained"
            onClick={() => void boot.mutateAsync({ instanceId: instance.id })}
            disabled={boot.isPending}
          >
            Retry
          </Button>
        </Paper>
      </Box>
    );
  }

  // --- Active (or paused) three-panel layout ---
  // Read-only sessions (served from the archive because the live gateway
  // is gone) disable chat regardless of instance status.
  //
  // Background-task sessions (headless via /api/v1/argus/run OR scheduled
  // via cron/one-shot) also disable chat: the agent has no human loop —
  // it runs its fixed prompt, emits `agent_end`, gets auto-killed. Any
  // user input would be ignored at the gateway. See
  // be/argus-ts/src/server.ts::autoKillApiSession.
  const isBackgroundTask =
    heartbeatSession?.run_origin === "headless" || heartbeatSession?.run_origin === "scheduled";
  // A session is "ended" once the backend has transitioned it to any
  // non-active status. Used to hide the Reconnect button + connection
  // chip on ended sessions — there's nothing to reconnect to, and the
  // chip's "disconnected" state is noise, not actionable.
  const isSessionEnded = Boolean(
    heartbeatSession?.session_status && heartbeatSession.session_status !== "active",
  );
  const scheduleInfo = detailSession?.schedule ?? null;
  const canChat = instance?.instance_status === "active" && !isReadOnly && !isBackgroundTask;
  const inputPlaceholder = isReadOnly
    ? "This session has ended."
    : isBackgroundTask
      ? heartbeatSession?.run_origin === "scheduled"
        ? "Scheduled background task — read-only."
        : "Headless background task — read-only."
      : !canChat
        ? instance?.instance_status === "paused"
          ? "Resume your instance to continue chatting"
          : "Argus is not ready yet"
        : activeSessionId
          ? "Ask Argus anything…"
          : "Start a new session by sending the first message";

  const handleSend = async (message: string, method?: "steer" | "followUp") => {
    try {
      if (!activeSessionId && !method) {
        track("argus_session_started", {
          message_preview: message.slice(0, 80),
        });
      }
      // When method is specified (followUp or steer), pass through directly.
      // Pi-coding-agent handles the queue natively — followUp queues for
      // after the turn, steer interrupts immediately.
      await sendMessage(message, method);
    } catch (err) {
      enqueueSnackbar(`Send failed: ${err instanceof Error ? err.message : "unknown error"}`, {
        variant: "error",
      });
    }
  };

  const handleStop = async () => {
    try {
      await stopSession();
    } catch (err) {
      enqueueSnackbar(`Stop failed: ${err instanceof Error ? err.message : "unknown error"}`, {
        variant: "error",
      });
    }
  };

  const centerPanel = (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        minWidth: 0,
        // Without minHeight:0 the children's natural height can grow past
        // the container, defeating the message list's internal `overflow: auto`.
        minHeight: 0,
      }}
    >
      <Box
        sx={{
          // Shared header height across all three panels keeps them on a
          // single horizontal baseline; on desktop the bottom divider is
          // drawn once as a continuous overlay in the PanelGroup parent
          // so it bridges the 4px resize handles between panels. On
          // mobile there's no PanelGroup, so draw it here directly.
          height: HEADER_HEIGHT,
          flexShrink: 0,
          paddingX: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          ...(isMobile && {
            borderBottom: "1px solid",
            borderColor: "divider",
          }),
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          {(isMobile || sessionsCollapsed) && (
            <>
              <Tooltip title="Show sessions" arrow>
                <IconButton
                  size="small"
                  onClick={() => {
                    if (isMobile) {
                      setSessionListDrawerOpen(true);
                    } else {
                      sessionsPanelRef.current?.expand();
                    }
                  }}
                  aria-label="Show sessions panel"
                >
                  <MenuIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="New session" arrow>
                <IconButton size="small" onClick={handleNewSession} aria-label="New session">
                  <AddIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          )}
          <ArgusInstanceControls instance={instance ?? null} />
        </Stack>
        {/* Active session name */}
        {activeSessionId && (
          <Typography
            variant="body2"
            sx={{
              flex: 1,
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              color: "text.secondary",
              fontSize: "0.8rem",
              textAlign: "center",
            }}
          >
            {stripContextPrefix(
              sessions?.find((s) => (s.agent_session_id ?? s.id) === activeSessionId)
                ?.first_message,
            ) || "New session"}
          </Typography>
        )}
        <Stack direction="row" spacing={1} alignItems="center">
          {/* Status-drift fallback: when the instance is DB-`active` but
              SSE has errored, the auto-heal effect in useArgusConnection
              has either fired (and is still in flight) or was throttled.
              This button is the user's escape hatch.

              Hide for (a) background tasks — the agent auto-kills on
              `agent_end` so "disconnected" is the expected terminal
              state, not an error; (b) sessions already in a terminal
              session_status — there's nothing to reconnect to. */}
          {instance &&
            instance.instance_status === "active" &&
            activeSessionId &&
            connectionError !== null &&
            !isReadOnly &&
            !isBackgroundTask &&
            !isSessionEnded && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<RefreshIcon />}
                disabled={heal.isPending}
                onClick={() => {
                  heal
                    .mutateAsync({ instanceId: instance.id })
                    .then(() => {
                      enqueueSnackbar("Reconnecting…", { variant: "info" });
                    })
                    .catch((err) => {
                      enqueueSnackbar(
                        `Reconnect failed: ${err instanceof Error ? err.message : "unknown error"}`,
                        { variant: "error" },
                      );
                    });
                }}
              >
                {heal.isPending ? "Reconnecting…" : "Reconnect"}
              </Button>
            )}
          {/* Connection chip only makes sense for live interactive
              sessions. Background tasks and ended sessions hide it —
              the chip's "disconnected" state is noise in those
              contexts, not actionable info. */}
          {!isBackgroundTask && !isSessionEnded && (
            <ArgusConnectionChip
              isConnected={isConnected}
              connectionError={connectionError}
              instanceStatus={instanceStatus}
              hasActiveSession={Boolean(activeSessionId)}
            />
          )}
          <Tooltip title="Show schedules" arrow>
            <IconButton
              size="small"
              onClick={() => setSchedulesDialogOpen(true)}
              aria-label="Show schedules"
            >
              <SchedulesIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {(isMobile || artifactsCollapsed) && (
            <Tooltip title="Show artifacts" arrow>
              <Badge badgeContent={artifactCount} color="primary" overlap="circular">
                <IconButton
                  size="small"
                  onClick={() => {
                    if (isMobile) {
                      setArtifactsDrawerOpen(true);
                    } else {
                      artifactsPanelRef.current?.expand();
                    }
                  }}
                  aria-label="Show artifacts panel"
                >
                  <ArtifactsIcon fontSize="small" />
                </IconButton>
              </Badge>
            </Tooltip>
          )}
        </Stack>
      </Box>
      {isBackgroundTask && (
        <Box
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: "center",
            paddingX: isMobile ? 1 : 2,
            paddingTop: 1,
          }}
        >
          <Box sx={{ width: "100%", maxWidth: 852 }}>
            <Alert
              severity={heartbeatSession?.run_origin === "scheduled" ? "warning" : "info"}
              variant="outlined"
              icon={false}
              sx={{ "& .MuiAlert-message": { width: "100%" } }}
            >
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <Chip
                  label={
                    heartbeatSession?.run_origin === "scheduled"
                      ? "Scheduled task"
                      : "Headless API task"
                  }
                  size="small"
                  color={heartbeatSession?.run_origin === "scheduled" ? "warning" : "info"}
                  sx={{ fontWeight: 600 }}
                />
                <AlertTitle sx={{ m: 0, fontSize: "0.875rem", flex: 1 }}>
                  Background task — read-only
                </AlertTitle>
                {detailSession?.report_s3_key && (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => setReportDrawerOpen(true)}
                    sx={{ flexShrink: 0 }}
                  >
                    View report
                  </Button>
                )}
              </Stack>
              <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
                {heartbeatSession?.run_origin === "scheduled"
                  ? "This session was started by a schedule. The agent runs its prompt, writes a report, and auto-terminates."
                  : "This session was started via the headless API. The agent runs its prompt, writes a report, and auto-terminates."}
              </Typography>
              {scheduleInfo && (
                <Box sx={{ mt: 1, fontSize: "0.8125rem" }}>
                  <Typography variant="caption" sx={{ color: "text.secondary", display: "block" }}>
                    <strong>Schedule:</strong> {scheduleInfo.name}
                  </Typography>
                  <Typography variant="caption" sx={{ color: "text.secondary", display: "block" }}>
                    <strong>Cadence:</strong>{" "}
                    {scheduleInfo.cron_expression
                      ? `cron \`${scheduleInfo.cron_expression}\` (UTC)`
                      : scheduleInfo.run_at
                        ? `one-shot at ${new Date(scheduleInfo.run_at).toLocaleString()}`
                        : "—"}{" "}
                    · trigger: {scheduleInfo.trigger}
                  </Typography>
                </Box>
              )}
            </Alert>
          </Box>
        </Box>
      )}
      {isReadOnly && (
        <Box
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: "center",
            paddingX: isMobile ? 1 : 2,
            paddingTop: 1,
          }}
        >
          <Box sx={{ width: "100%", maxWidth: 852 }}>
            <Alert severity="info" variant="outlined">
              This session has ended.
            </Alert>
          </Box>
        </Box>
      )}
      <ArgusMessageList
        events={events}
        seededMessages={seededMessages}
        optimisticUserMessages={optimisticUserMessages}
        isConnected={isConnected}
        isAwaitingResponse={isAwaitingResponse}
        isTranscriptLoading={isTranscriptLoading}
        hydratedOpenToolCallIds={hydratedOpenToolCallIds}
        isOnboardingSession={isOnboardingSession}
      />
      {/* Cap the input to the same max-width as the transcript column
          (see ArgusMessageList) so the textarea aligns with the bubbles on
          wide screens instead of spanning the full panel. On mobile we
          drop the outer padding entirely so the input hugs the viewport
          edge — ArgusMessageInput adds its own small inset. */}
      <Box
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: "center",
          paddingX: isMobile ? 0 : 2,
        }}
      >
        <Box sx={{ width: "100%", maxWidth: 852 }}>
          <ArgusMessageInput
            disabled={!canChat}
            placeholder={inputPlaceholder}
            onSend={handleSend}
            isAgentWorking={isAgentWorking}
            wasStopped={wasStopped}
            queuedMessages={queuedMessages}
            onRemoveFromQueue={removeFromQueue}
            onPromoteToSteer={promoteToSteer}
            onClearQueue={clearQueue}
            onStop={handleStop}
            contextTokens={liveContextTokens ?? detailSession?.context_tokens}
            contextWindow={detailSession?.context_window}
          />
        </Box>
      </Box>
    </Box>
  );

  // Shared session-list content — rendered in the inline Panel on desktop
  // and inside the mobile-only Drawer below. Wrapped select / new-session
  // handlers close the mobile drawer after a selection.
  const sessionListContent = (
    <ArgusSessionListPanel
      sessions={sessions ?? []}
      activeSessionId={activeSessionId}
      pendingSessionPreview={pendingSessionPreview}
      instance={instance}
      onSelect={(id) => {
        handleSelectSession(id);
        if (isMobile) setSessionListDrawerOpen(false);
      }}
      onNewSession={() => {
        handleNewSession();
        if (isMobile) setSessionListDrawerOpen(false);
      }}
      onCollapse={() => {
        if (isMobile) {
          setSessionListDrawerOpen(false);
        } else {
          sessionsPanelRef.current?.collapse();
        }
      }}
      onUpdate={
        instance?.update_available && instance?.id && !update.isPending
          ? () => setConfirmUpdateOpen(true)
          : undefined
      }
      isUpdating={update.isPending}
    />
  );

  // Shared artifacts content — rendered in the inline Panel on desktop and
  // inside the mobile-only Drawer below.
  const activeSession = sessions?.find((s) => (s.agent_session_id ?? s.id) === activeSessionId);
  // Artifact fetches key on the pi agent session id — the gateway
  // stamps `created_by = agent:<agent_session_id>` on every
  // session-scoped artifact. Falls back to `activeSession.id` for
  // legacy rows that pre-date the split.
  const activeAgentSessionId = activeSession?.agent_session_id ?? activeSession?.id ?? null;
  const artifactsPanelContent = (
    <ArgusRelatedResourcesPanel
      events={events}
      sessionId={activeSessionId}
      agentSessionId={activeAgentSessionId}
      instanceId={instanceId ?? null}
      onTotalCountChange={setArtifactCount}
      onCollapse={() => {
        if (isMobile) {
          setArtifactsDrawerOpen(false);
        } else {
          artifactsPanelRef.current?.collapse();
        }
      }}
    />
  );

  if (isMobile) {
    return (
      <Box sx={{ flex: 1, minHeight: 0, position: "relative" }}>
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          {centerPanel}
        </Box>
        <Drawer
          anchor="left"
          open={sessionListDrawerOpen}
          onClose={() => setSessionListDrawerOpen(false)}
          PaperProps={{
            sx: { width: "min(320px, 90vw)" },
          }}
        >
          {sessionListContent}
        </Drawer>
        <Drawer
          anchor="right"
          open={artifactsDrawerOpen}
          onClose={() => setArtifactsDrawerOpen(false)}
          PaperProps={{
            sx: { width: "min(420px, 100vw)" },
          }}
        >
          {artifactsPanelContent}
        </Drawer>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        // The dashboard Outlet wrapper uses `min-height: 100%` which does
        // NOT count as a definite height for CSS percent resolution. Anchor
        // the PanelGroup with absolute positioning so it inherits an exact
        // pixel height — required for the message list's internal scroll
        // (`flex: 1 + minHeight: 0 + overflow: auto`) to actually kick in.
        position: "relative",
      }}
    >
      {/* Continuous horizontal divider bridging the 4px resize handles
          between panels. The per-panel header `borderBottom` is
          interrupted by each handle's width, leaving visible gaps where
          the center column's divider meets the side columns' dividers. */}
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          top: HEADER_HEIGHT - 1,
          left: 0,
          right: 0,
          height: "1px",
          backgroundColor: "divider",
          pointerEvents: "none",
          zIndex: 1,
        }}
      />
      <PanelGroup
        direction="horizontal"
        autoSaveId="argus-layout"
        style={{ position: "absolute", inset: 0 }}
      >
        <Panel
          ref={sessionsPanelRef}
          // Initialise at `defaultSize=0` when `?sessions=0` is present so
          // the panel is already collapsed on first paint — belt-and-braces
          // with the `.collapse()` effect above so `autoSaveId` can't win
          // the race and leave it visible for one frame.
          defaultSize={searchParams.get("sessions") === "0" ? 0 : 18}
          minSize={14}
          maxSize={30}
          collapsible
          collapsedSize={0}
          onCollapse={() => {
            setSessionsCollapsed(true);
            setSearchParams(
              (prev) => {
                const next = new URLSearchParams(prev);
                next.set("sessions", "0");
                return next;
              },
              { replace: true },
            );
          }}
          onExpand={() => {
            setSessionsCollapsed(false);
            setSearchParams(
              (prev) => {
                const next = new URLSearchParams(prev);
                next.delete("sessions");
                return next;
              },
              { replace: true },
            );
          }}
        >
          {sessionListContent}
        </Panel>
        <PanelResizeHandle
          style={{
            width: 4,
            background: "transparent",
            cursor: "col-resize",
          }}
        />
        <Panel minSize={30}>{centerPanel}</Panel>
        <PanelResizeHandle
          style={{
            width: 4,
            background: "transparent",
            cursor: "col-resize",
          }}
        />
        <Panel
          ref={artifactsPanelRef}
          defaultSize={24}
          minSize={18}
          maxSize={40}
          collapsible
          collapsedSize={0}
          onCollapse={() => {
            setArtifactsCollapsed(true);
            setSearchParams(
              (prev) => {
                const next = new URLSearchParams(prev);
                next.delete("artifacts");
                return next;
              },
              { replace: true },
            );
          }}
          onExpand={() => {
            setArtifactsCollapsed(false);
            setSearchParams(
              (prev) => {
                const next = new URLSearchParams(prev);
                next.set("artifacts", "1");
                return next;
              },
              { replace: true },
            );
          }}
        >
          {artifactsPanelContent}
        </Panel>
      </PanelGroup>
      <Dialog
        open={confirmUpdateOpen}
        onClose={() => {
          if (!update.isPending) setConfirmUpdateOpen(false);
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Update Argus?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will restart the sandbox and end any running session. Continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmUpdateOpen(false)} disabled={update.isPending}>
            Cancel
          </Button>
          <Button
            color="warning"
            variant="contained"
            disabled={!instance?.id || update.isPending}
            onClick={async () => {
              if (!instance?.id) return;
              try {
                await update.mutateAsync({ instanceId: instance.id });
                enqueueSnackbar("Update started — sandbox is rebooting", {
                  variant: "info",
                });
              } catch (err) {
                enqueueSnackbar(
                  `Update failed: ${err instanceof Error ? err.message : "unknown error"}`,
                  { variant: "error" },
                );
              } finally {
                setConfirmUpdateOpen(false);
              }
            }}
            startIcon={
              update.isPending ? <CircularProgress size={14} color="inherit" /> : undefined
            }
          >
            {update.isPending ? "Updating…" : "Update"}
          </Button>
        </DialogActions>
      </Dialog>
      <ArgusReportDrawer
        sessionId={heartbeatSession?.id}
        open={reportDrawerOpen}
        onClose={() => setReportDrawerOpen(false)}
        isLive={heartbeatSession?.session_status === "active"}
      />
      <ArgusSchedulesDialog
        open={schedulesDialogOpen}
        onClose={() => setSchedulesDialogOpen(false)}
        instanceId={instanceId}
        onNavigateToSession={(sessionId) => {
          setActiveSessionId(sessionId);
          setSchedulesDialogOpen(false);
        }}
      />
    </Box>
  );
};

/**
 * Zero-state helper — no Argus instance for this org.
 *
 * Renders when onboarding is complete but the Argus instance was deleted
 * (admin action / migration). Minimal empty-state with a single link that
 * re-provisions via the onboarding endpoint. Dashboard layout gates the
 * mid-onboarding case — per `new-fe/CLAUDE.md`, don't add a per-page guard.
 */
const ArgusZeroStateEmpty = () => {
  const { enqueueSnackbar } = useSnackbar();
  const provision = useApiMutation<void, OnboardingProvisionResponse>(
    "POST",
    "/client/argus/onboarding/provision",
  );

  const handleReprovision = async () => {
    try {
      await provision.mutateAsync({});
      // The status endpoint and instance list poll / invalidate on success,
      // so the parent `useArgusInstances` query will re-fetch and the page
      // transitions out of zero-state on its own.
      enqueueSnackbar("Provisioning your workspace…", { variant: "info" });
    } catch (err) {
      enqueueSnackbar(
        `Could not provision workspace: ${err instanceof Error ? err.message : "unknown error"}`,
        { variant: "error" },
      );
    }
  };

  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 4,
      }}
    >
      <Paper
        variant="outlined"
        sx={{
          maxWidth: 440,
          padding: 4,
          borderRadius: `${RADIUS_LG}px`,
          textAlign: "center",
        }}
      >
        <DespleaIcon sx={{ fontSize: 40, color: "primary.main", marginBottom: 1 }} />
        <Typography variant="subtitle1" sx={{ marginBottom: 1, fontWeight: 600 }}>
          No Argus instances yet
        </Typography>
        <Typography variant="body2" sx={{ color: "text.secondary", marginBottom: 2 }}>
          Your workspace is empty. Spin up a fresh Argus instance to continue.
        </Typography>
        <Button
          variant="text"
          size="small"
          onClick={() => void handleReprovision()}
          disabled={provision.isPending}
          sx={{ textTransform: "none" }}
        >
          {provision.isPending ? "Creating…" : "Create your workspace"}
        </Button>
      </Paper>
    </Box>
  );
};

export default ArgusPage;
